import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { BoardStatus } from '@/types'
import type { AxiosError } from 'axios'
import api from '../../services/api'

interface StatusListPayload {
  statuses: BoardStatus[]
}

interface StatusPayload {
  status: BoardStatus
}

interface ReorderPayload {
  statuses: BoardStatus[]
}

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }
  if ((error as AxiosError)?.response?.data) {
    const data = (error as AxiosError<{ message?: string }>).response?.data
    if (data?.message) {
      return data.message
    }
  }
  return fallback
}

const sortStatusesByPosition = (statuses: BoardStatus[]) =>
  [...statuses].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

interface BoardStatusState {
  statuses: BoardStatus[]
  loading: boolean
  error: string | null
}

const initialState: BoardStatusState = {
  statuses: [],
  loading: false,
  error: null,
}

// 獲取專案狀態
export const fetchProjectStatuses = createAsyncThunk<
  BoardStatus[],
  string,
  { rejectValue: string }
>('boardStatus/fetchProjectStatuses', async (projectId, thunkAPI) => {
  try {
    const response = await api.get<{ data: StatusListPayload }>(
      `/board/projects/${projectId}/statuses`,
    )
    return sortStatusesByPosition(response.data.data.statuses)
  } catch (error) {
    return thunkAPI.rejectWithValue(resolveErrorMessage(error, '獲取狀態失敗'))
  }
})

// 初始化預設狀態
export const initializeDefaultStatuses = createAsyncThunk<
  BoardStatus[],
  string,
  { rejectValue: string }
>('boardStatus/initializeDefaultStatuses', async (projectId, thunkAPI) => {
  try {
    const response = await api.post<{ data: StatusListPayload }>(
      `/board/projects/${projectId}/statuses/initialize`,
    )
    return sortStatusesByPosition(response.data.data.statuses)
  } catch (error) {
    return thunkAPI.rejectWithValue(resolveErrorMessage(error, '初始化狀態失敗'))
  }
})

// 創建新狀態
interface CreateStatusArgs {
  projectId: string
  statusData: {
    name: string
    color?: string
    isDefault?: boolean
  }
}

export const createStatus = createAsyncThunk<
  BoardStatus,
  CreateStatusArgs,
  { rejectValue: string }
>('boardStatus/createStatus', async ({ projectId, statusData }, thunkAPI) => {
  try {
    const response = await api.post<{ data: StatusPayload }>(
      `/board/projects/${projectId}/statuses`,
      statusData,
    )
    return response.data.data.status
  } catch (error) {
    return thunkAPI.rejectWithValue(resolveErrorMessage(error, '創建狀態失敗'))
  }
})

// 更新狀態
interface UpdateStatusArgs {
  projectId: string
  statusId: string
  updateData: {
    name?: string
    color?: string
    isDefault?: boolean
  }
}

export const updateStatus = createAsyncThunk<
  BoardStatus,
  UpdateStatusArgs,
  { rejectValue: string }
>('boardStatus/updateStatus', async ({ projectId, statusId, updateData }, thunkAPI) => {
  try {
    const response = await api.put<{ data: StatusPayload }>(
      `/board/projects/${projectId}/statuses/${statusId}`,
      updateData,
    )
    return response.data.data.status
  } catch (error) {
    return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新狀態失敗'))
  }
})

// 重新排序狀態
interface ReorderArgs {
  projectId: string
  statusIds: string[]
}

export const reorderStatuses = createAsyncThunk<
  BoardStatus[],
  ReorderArgs,
  { rejectValue: string }
>('boardStatus/reorderStatuses', async ({ projectId, statusIds }, thunkAPI) => {
  try {
    const response = await api.put<{ data: ReorderPayload }>(
      `/board/projects/${projectId}/statuses/reorder`,
      { statusIds },
    )
    return sortStatusesByPosition(response.data.data.statuses)
  } catch (error) {
    return thunkAPI.rejectWithValue(resolveErrorMessage(error, '重新排序失敗'))
  }
})

// 刪除狀態
interface DeleteStatusArgs {
  projectId: string
  statusId: string
  moveToStatusId?: string
}

export const deleteStatus = createAsyncThunk<
  string,
  DeleteStatusArgs,
  { rejectValue: string }
>('boardStatus/deleteStatus', async ({ projectId, statusId, moveToStatusId }, thunkAPI) => {
  try {
    await api.delete(`/board/projects/${projectId}/statuses/${statusId}`, {
      data: moveToStatusId ? { moveToStatusId } : {},
    })
    return statusId
  } catch (error) {
    return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除狀態失敗'))
  }
})

const boardStatusSlice = createSlice({
  name: 'boardStatus',
  initialState,
  reducers: {
    clearStatuses: (state) => {
      state.statuses = []
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // 獲取專案狀態
      .addCase(fetchProjectStatuses.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProjectStatuses.fulfilled, (state, action) => {
        state.loading = false
        state.statuses = action.payload
      })
      .addCase(fetchProjectStatuses.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || action.error.message || '獲取狀態失敗'
      })

      // 初始化預設狀態
      .addCase(initializeDefaultStatuses.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(initializeDefaultStatuses.fulfilled, (state, action) => {
        state.loading = false
        state.statuses = action.payload
      })
      .addCase(initializeDefaultStatuses.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || action.error.message || '初始化狀態失敗'
      })

      // 創建新狀態
      .addCase(createStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createStatus.fulfilled, (state, action) => {
        state.loading = false
        state.statuses = sortStatusesByPosition([...state.statuses, action.payload])
      })
      .addCase(createStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || action.error.message || '創建狀態失敗'
      })

      // 更新狀態
      .addCase(updateStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateStatus.fulfilled, (state, action) => {
        state.loading = false
        const index = state.statuses.findIndex(s => s.id === action.payload.id)
        if (index !== -1) {
          state.statuses[index] = action.payload
        }
      })
      .addCase(updateStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || action.error.message || '更新狀態失敗'
      })

      // 重新排序狀態
      .addCase(reorderStatuses.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(reorderStatuses.fulfilled, (state, action) => {
        state.loading = false
        state.statuses = action.payload
      })
      .addCase(reorderStatuses.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || action.error.message || '重新排序失敗'
      })

      // 刪除狀態
      .addCase(deleteStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteStatus.fulfilled, (state, action) => {
        state.loading = false
        state.statuses = state.statuses.filter(s => s.id !== action.payload)
      })
      .addCase(deleteStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || action.error.message || '刪除狀態失敗'
      })
  },
})

export const { clearStatuses, clearError } = boardStatusSlice.actions
export default boardStatusSlice.reducer
